// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import usePermission from "@/hooks/usePermission";
import useTranslation from "@/hooks/useTranslation";
import type { TyIconName } from "@/UI/atoms/Icon/types";
// --- Logic --------------------------------------------------------------------------------------
export type TyNavTab = {
    path: string;
    model: string;
    label: string;
    icon: TyIconName;
    /** No endpoints behind it yet — the surfaces mark it, and its route renders `PendingScreen`. */
    isPending: boolean;
};

/**
 * The dashboard's destinations, already filtered by what this admin may open. The sidebar, the phone
 * bar and the "more" sheet all read this one list, so a permission can never show a tab on one
 * surface and hide it on another.
 *
 * `footerTabs` is the second group — managing Lozicore's own team is housekeeping, not the work, so it
 * sits at the foot of the sidebar rather than in the run of daily sections. The profile is not here at
 * all: it lives in the account popup beside signing out.
 */
export default function useNavTabs(): { tabs: TyNavTab[]; footerTabs: TyNavTab[] } {
    const t = useTranslation();
    const { canAccess } = usePermission();

    const tabs: Omit<TyNavTab, "isPending">[] = [
        { path: "/", model: "home", label: t.sideBar.home, icon: "home" },
        { path: "/organizations", model: "organizations", label: t.sideBar.organizations, icon: "building" },
        { path: "/subscriptions", model: "subscriptions", label: t.sideBar.subscriptions, icon: "subscription" },
        { path: "/clients", model: "clients", label: t.sideBar.clients, icon: "team" },
        { path: "/enquiries", model: "enquiries", label: t.sideBar.enquiries, icon: "inbox" },
        { path: "/support", model: "support", label: t.sideBar.support, icon: "support" },
    ];

    const footerTabs: Omit<TyNavTab, "isPending">[] = [{ path: "/admins", model: "admins", label: t.sideBar.admins, icon: "crown" }];

    const prepare = (list: Omit<TyNavTab, "isPending">[]): TyNavTab[] =>
        list.filter((tab) => canAccess(tab.model)).map((tab) => ({ ...tab, isPending: constants.pendingModels.includes(tab.model) }));

    return { tabs: prepare(tabs), footerTabs: prepare(footerTabs) };
}
